import { parseISO } from 'date-fns'
import dayjs from 'dayjs'
import { useEffect } from 'react'
import { Helmet } from 'react-helmet-async'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import styled from 'styled-components'
import api from '../../../api'
import Responsive from '../../../assets/config/Responsive'
import Colors from '../../../assets/styles/Colors'
import { Container } from '../../../assets/styles/GlobalStyle'
import Loader from '../../../components/Loader'
import BackgroundIcon from '../../../components/v2/BackgroundIcon'
import DatePicker from '../../../components/v2/DatePicker'
import InfoMessage from '../../../components/v2/InfoMessage'
import RemainingCompanies from '../../../components/v2/RemainingCompanies'
import ScreenHeader from '../../../components/v2/ScreenHeader'
import Title from '../../../components/v2/Title'
import NavigationButton from '../../../components/v2/buttons/NavigationButton'
import PetProgressbar from '../../../components/v2/progrees-bars/PetProgressBar'
import { useAppSelector } from '../../../store'
import { AppState } from '../../../store/appSlice'
import { PetState, setPetDates } from '../../../store/petSlice'

const PetStartDate = () => {
  //
  const dispatch = useDispatch()
  //
  const { t } = useTranslation()
  //
  const navigate = useNavigate()
  //
  const { lang } = useAppSelector((root: { app: AppState }) => root.app)
  //
  const { filter, screenHeader } = useAppSelector((data: { pet: PetState }) => data.pet)
  //
  const maxDate = dayjs(filter.dates?.start).add(365, 'day').format('YYYY-MM-DD')
  //
  const { data: pageInfo, isLoading: isPageInfoLoading } = api.useGetPetPageInfoQuery('pet-insurance-start-date')
  //
  const pageTitle = pageInfo?.translations[lang]?.title
  const stepName = pageInfo?.translations[lang]?.stepName
  const firstItem = pageInfo?.listItems?.[0]
  //
  // auto navigate first page
  useEffect(() => {
    if (filter?.petCategoryId === null) {
      navigate('/choose-pet')
    }
    //
    return () => {}
  }, [filter?.petCategoryId, navigate])
  //
  const [getLeftCompanies, { data: leftCompanies }] = api.usePetLeftCompaniesMutation()
  //
  useEffect(() => {
    if (
      filter.petCategoryId &&
      (filter.breedId || filter.breedId === null) &&
      ((filter?.petInfo?.month !== null && filter?.petInfo?.month !== 0) ||
        (filter?.petInfo?.age !== null && filter?.petInfo?.age !== 0)) &&
      (filter?.breedId === null ? filter?.petInfo?.breedOther !== '' : true) &&
      filter.dates.start &&
      filter.dates.end
    ) {
      const GetCompaniesFunction = async () => {
        try {
          await getLeftCompanies({
            step: 'two',
            animalCategoryId: filter?.petCategoryId as number,
            breedId: filter.breedId,
            petAge: {
              month: filter?.petInfo?.month as number,
              year: filter?.petInfo?.age as number,
            },
            otherBreedName: filter?.breedId === null ? filter?.petInfo?.breedOther : '',
            startDate: filter.dates.start as string,
            endDate: filter.dates.end as string,
          }).unwrap()
        } catch (error: any) {
          if (error?.data?.errors?.constructor === Array) {
            const firstError = error.data.errors[0]
            const errorMessage = firstError.message
            toast.error(errorMessage)
          } else {
            toast.error('Მოხდა შეცდომა. Გთხოვთ სცადოთ მოგვიანებით... (GetCompaniesFunction) (single)')
          }
        }
      }
      //
      GetCompaniesFunction()
    }

    return () => {}
  }, [
    getLeftCompanies,
    filter?.breedId,
    filter?.petCategoryId,
    filter?.petInfo?.age,
    filter?.petInfo?.month,
    filter?.dates.start,
    filter?.dates?.end,
    filter?.petInfo?.breedOther,
  ])
  //
  //
  const MainLoading = isPageInfoLoading
  //
  return MainLoading ? (
    <Loader />
  ) : (
    <Wrapper>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageTitle} />
        <link rel="canonical" href={window.location.href} />
      </Helmet>

      <ScreenHeader totalCount={screenHeader.totalCount} passedCount={screenHeader.passedCount} />

      <Container>
        <Content>
          <Title text={pageTitle} />

          <PetProgressbar stepName={stepName} />

          <WhiteBox>
            <DatePicker
              minDate={new Date()}
              maxDate={parseISO(maxDate) || null}
              className="start-date-class"
              label={`${t('startDate')}`}
              dateFormat="dd MMMM yyyy"
              open={true}
              selectedDate={filter?.dates?.start ? parseISO(filter?.dates?.start as string) : null}
              onchange={(value) => {
                //
                const startDate = dayjs(value).format('YYYY-MM-DD')
                const endDate = dayjs(value).add(365, 'day').format('YYYY-MM-DD')
                //
                dispatch(
                  setPetDates({
                    start: startDate,
                    end: endDate,
                  }),
                )
              }}
            />
          </WhiteBox>

          {filter?.dates?.start ? (
            <InfoMessage
              style={{
                marginInline: 'auto',
                maxWidth: '464px',
                marginTop: '24px',
              }}
              background="gray"
              variant="info"
              title={firstItem?.translations?.[lang]?.title}
              text={firstItem?.translations?.[lang]?.body}
            />
          ) : null}

          <RemainingCompanies
            style={{
              opacity: leftCompanies ? '1' : '0',
              marginInline: 'auto',
            }}
            text={`${t('offerAvailable')}: ${leftCompanies?.companiesCount} `}
          />

          <Buttons>
            <NavigationButton text={`${t('back')}`} buttonType="prev" onClick={() => navigate('/pet-info')} />
            <NavigationButton
              disabled={
                filter.dates &&
                filter.dates.start &&
                filter.dates.end &&
                leftCompanies &&
                leftCompanies?.companiesCount &&
                leftCompanies?.companiesCount > 0
                  ? false
                  : true
              }
              text={`${t('next')}`}
              buttonType="next"
              onClick={() => navigate('/pet-offer')}
            />
          </Buttons>
        </Content>
      </Container>
      <BackgroundIcon />
    </Wrapper>
  )
}
//
//
const WhiteBox = styled.div`
  background-color: ${Colors.white};
  box-shadow: 0 4px 50px 0 ${Colors.shadow};
  border-radius: 8px;
  width: 100%;
  max-width: 464px;
  margin-inline: auto;
  padding: 34px 48px;
  margin-block: 32px;

  .react-datepicker-wrapper {
    width: 100%;
  }

  .react-datepicker-popper {
    position: unset !important;
    transform: unset !important;
    margin-top: 10px;
  }
  .react-datepicker {
    display: flex;
    justify-content: center;
  }

  ${Responsive.mobile} {
    padding: 32px 20px;
    margin-bottom: 24px;

    &:first-child {
      margin-top: 24px;
      margin-bottom: 20px;
    }
  }
`
const Buttons = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-inline: auto;
  width: 100%;
  margin-top: 48px;
  max-width: 368px;

  button {
    width: 100%;
    padding-inline: 0px;
    &:first-child {
      margin-right: 20px;
    }
  }

  ${Responsive.mobile} {
    max-width: 296px;
    margin-top: 40px;
    button {
      &:first-child {
        margin-right: 15px;
      }
    }
  }
`
const Content = styled.div`
  padding-block: 60px;
  position: sticky;
  z-index: 1;

  ${Responsive.mobile} {
    padding-block: 30px;
  }
`
const Wrapper = styled.div`
  /*  */
`
//
export default PetStartDate
